import { useCallback, useLayoutEffect, useRef, useState } from "react";

// Tier-3 builder presentation seam: the reveal used by every editor body that
// opens and closes -- a single block, a cardio block, a superset lane, and the
// superset's shared controls.
//
// It exists because the exit needs the closing content to survive one animation
// longer than the product state does. `openBlockId` and
// `openSupersetExerciseByBlock` still close IMMEDIATELY: this only lets the
// renderer finish drawing what was already on screen. The closing region is
// `aria-hidden` and `inert` from the first frame of the exit, so nothing inside
// it is reachable, focusable or announced once it is visually closing.
//
// Removal is driven by `animationend`, so the region leaves the instant its exit
// finishes and never a moment later. Two things stand behind that event, because
// a closed editor must never be left mounted waiting for one that does not come:
// when there is no animation at all (`prefers-reduced-motion: reduce`, or any
// environment where the stylesheet is not applied) the region is dropped in the
// same commit, and otherwise a fallback sized from the element's OWN computed
// duration covers a document hidden mid-exit, where animations are throttled and
// the event can stall indefinitely.
//
// `children` is a function so a closed region costs nothing to render.

// One frame's worth of slack over the element's own animation duration, so the
// net never races the `animationend` it is standing in for.
const EXIT_SAFETY_MARGIN_MS = 50;

// The exit has its own @keyframes, which is what makes it a real animation
// rather than the finished reveal re-evaluated backwards. Matching on the name
// keeps an unrelated animation inside the editor from ending the exit early.
const EXIT_ANIMATION_NAME = "lz-builder-reveal-exit";

// How long this element's own exit will take, read off the element rather than
// assumed: 0 when there is nothing to wait for.
function readExitDurationMs(element) {
  if (!element || typeof window === "undefined" || !window.getComputedStyle) {
    return 0;
  }

  const styles = window.getComputedStyle(element);

  if (!styles.animationName || styles.animationName === "none") {
    return 0;
  }

  return (styles.animationDuration || "")
    .split(",")
    .reduce((longest, duration) => {
      const seconds = Number.parseFloat(duration);
      return Number.isFinite(seconds) ? Math.max(longest, seconds * 1000) : longest;
    }, 0);
}

export function useRevealPresence(open) {
  const nodeRef = useRef(null);
  // "open" while the region is live, "closing" for the single exit animation,
  // null once it is gone.
  const [presence, setPresence] = useState(open ? "open" : null);

  if (open && presence !== "open") {
    // Re-opening mid-exit simply returns to the open state; swapping the
    // animation restarts it from the collapsed frame it had reached.
    setPresence("open");
  }

  if (!open && presence === "open") {
    setPresence("closing");
  }

  useLayoutEffect(() => {
    if (presence !== "closing") {
      return undefined;
    }

    const exitMs = readExitDurationMs(nodeRef.current);

    if (exitMs === 0) {
      setPresence(null);
      return undefined;
    }

    // `animationend` is the signal -- it removes the region the instant the exit
    // finishes, with no added delay. This is only the net beneath it: a hidden
    // document throttles animations and may never deliver that event, and a
    // closed editor must not be left mounted because of it. The wait is the
    // element's own duration, read from the element, not a guess.
    const timerId = window.setTimeout(
      () => setPresence((current) => (current === "closing" ? null : current)),
      exitMs + EXIT_SAFETY_MARGIN_MS
    );

    return () => window.clearTimeout(timerId);
  }, [presence]);

  const handleAnimationEnd = useCallback((event) => {
    // Only this region's own exit ends it -- never the reveal, and never an
    // animation bubbling up from something inside the editor.
    if (
      event.target !== nodeRef.current ||
      event.animationName !== EXIT_ANIMATION_NAME
    ) {
      return;
    }

    setPresence((current) => (current === "closing" ? null : current));
  }, []);

  return {
    nodeRef,
    presence,
    shouldRender: presence !== null,
    handleAnimationEnd,
  };
}

export default function BlockRevealRegion({ open, children }) {
  const { nodeRef, presence, shouldRender, handleAnimationEnd } =
    useRevealPresence(open);

  if (!shouldRender) {
    return null;
  }

  const isClosing = presence === "closing";

  return (
    <div
      ref={nodeRef}
      className="lz-builder-reveal"
      data-state={presence}
      aria-hidden={isClosing ? "true" : undefined}
      inert={isClosing ? "" : undefined}
      onAnimationEnd={handleAnimationEnd}
    >
      <div className="lz-builder-reveal__inner">{children()}</div>
    </div>
  );
}
